import { Button, Checkbox, Form, Input, notification } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useGetAdminsInfoQuery, useUpdateAdminMutation } from 'app/services/edpApiV1/admins';
import RoleInput from 'features/controls/addAdmin/roleInput/RoleInput';
import React, { FC, useEffect, useState } from 'react';
import { AdminPatchRequest, AdminsDataPrepared } from 'shared/types/admins.types';
import { TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import OrganizationsInput from 'features/reports/form-record/organizationsInput/OrganizationsInput';
import ModerationCheckboxes from 'features/controls/moderationCheckboxes/ModerationCheckboxes';
import useResponse from '../response-handle/useResponse';
import styles from './index.module.scss';

type TAdminUpdateFormProps = {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  admin: AdminsDataPrepared | null;
};

enum UpdateAdminFormFields {
  EMAIL = 'email',
  NEWS_MODERATOR = 'newsModerator',
  POLL_MODERATOR = 'pollModerator',
  FAQ_MODERATOR = 'faqModerator',
  SUPERVISOR = 'supervisor',
  DEPUTY = 'deputy',
  ACTING = 'acting',
  TIMEKEEPER = 'timekeeper',
  ROLE = 'role',
  ORGANIZATION_IDS = 'organizationIds',
}

const UpdateAdminForm: FC<TAdminUpdateFormProps> = ({ setIsModalOpen, admin }) => {
  const [form] = useForm();
  const [email, setEmail] = useState<string>('');
  const [
    updateAdminQuery,
    {
      data: updateResult,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
      isLoading: isUpdateLoading,
    },
  ] = useUpdateAdminMutation();

  const { data: adminInfo, isLoading: adminInfoFetching } = useGetAdminsInfoQuery({ id: admin?.id || 0 });

  useEffect(() => {
    form.setFieldValue(UpdateAdminFormFields.EMAIL, admin?.email);
    form.setFieldValue(UpdateAdminFormFields.FAQ_MODERATOR, admin?.faqModerator ?? false);
    form.setFieldValue(UpdateAdminFormFields.NEWS_MODERATOR, admin?.newsModerator ?? false);
    form.setFieldValue(UpdateAdminFormFields.POLL_MODERATOR, admin?.pollModerator ?? false);
    form.setFieldValue(UpdateAdminFormFields.ACTING, admin?.acting ?? false);
    form.setFieldValue(UpdateAdminFormFields.DEPUTY, admin?.deputy ?? false);
    form.setFieldValue(UpdateAdminFormFields.TIMEKEEPER, admin?.timekeeper ?? false);
    form.setFieldValue(UpdateAdminFormFields.SUPERVISOR, admin?.supervisor ?? false);
  }, [admin, form]);

  useEffect(() => {
    if (adminInfo) {
      form.setFieldValue(UpdateAdminFormFields.ROLE, adminInfo?.role.roleId);
      form.setFieldValue(
        UpdateAdminFormFields.ORGANIZATION_IDS,
        adminInfo?.organizations.map(elem => elem.id),
      );
    }
  }, [adminInfo]);

  useResponse({
    isUpdateError,
    isUpdateSuccess,
    updateError,
    updateResult,
  });

  const onPickAll = (organizations: TOrganizationResponse[]) => {
    const organizationsIds: string[] = organizations
      .filter(({ id }) => id != null)
      .map(elem => {
        return elem.id;
      });
    form.setFieldValue(UpdateAdminFormFields.ORGANIZATION_IDS, organizationsIds);
    form.validateFields();
  };

  const onFinish = async (values: AdminPatchRequest) => {
    const isHaveOwnOrganization = !!values.organizationIds.find(elem => elem === admin?.organizationId);

    if (!isHaveOwnOrganization) {
      notification.error({ message: 'Нельзя удалить из списка текущую организацию' });
      return;
    }
    await updateAdminQuery({ ...values, employeeId: admin?.id || 0 });
    Object.keys(form.getFieldsValue()).forEach(key => {
      form.setFieldValue(key, undefined);
    });
    setIsModalOpen(false);
  };
  return (
    <Form
      form={form}
      name="basic"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      style={{ maxWidth: 800 }}
      initialValues={{ remember: true }}
      onFinish={onFinish}
      autoComplete="off"
    >
      <RoleInput loading={adminInfoFetching} />
      <OrganizationsInput
        onPickAll={onPickAll}
        label="Организации администратора"
        placeholder="Выберите подконтрольные администратору организации"
        rules={[{ required: true, message: 'Введите организации!' }]}
        loading={adminInfoFetching}
      />
      <Form.Item
        label="Email"
        name={UpdateAdminFormFields.EMAIL}
        style={{ marginBottom: 10 }}
        rules={[
          { required: true, message: 'Введите email!' },
          { type: 'email', message: 'email не валидный' },
        ]}
      >
        <Input onChange={e => setEmail(e.target.value)} />
      </Form.Item>
      <ModerationCheckboxes />
      <Form.Item className={styles.button}>
        <Button type="primary" htmlType="submit" loading={isUpdateLoading}>
          Сохранить
        </Button>
      </Form.Item>
    </Form>
  );
};

export default UpdateAdminForm;
